import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Alert, Box, Button, CircularProgress, Slider, Stack, TextField, Typography } from '@mui/material';

import ProximityMap from '~/components/Attractions/ProximityMap';

import { useGetNearbyHotelOfferImages } from '~/hooks/apis/search/useGetNearbyHotelOfferImages';
import { useGetNearbyHotelOffers } from '~/hooks/apis/search/useGetNearbyHotelOffers';

import type { NearbyHotelOffersParams } from '~/services/SearchService';
import type { NearbyHotelOffer } from '~/services/SearchService';

import type { ExperienceOfferItem } from '../../hooks';
import TagAssignmentsOfferList from '../TagAssignmentsOfferList';

const DEFAULT_RADIUS_KM = 20;
const MIN_RADIUS_KM = 1;
const MAX_RADIUS_KM = 40;
const METERS_PER_KILOMETRE = 1000;

interface Props {
  experienceOfferId?: string;
  experienceOffer?: ExperienceOfferItem;
  selectedOfferIds: Array<string>;
  savedOfferIds: Array<string>;
  onSelectionChange: (selectedIds: Array<string>) => void;
}

function parseCoordinate(value: string): number | null {
  if (value.trim() === '') {
    return null;
  }

  const coordinate = Number(value);
  return Number.isFinite(coordinate) ? coordinate : null;
}

function isValidLatitude(latitude: number | null): latitude is number {
  return latitude !== null && latitude >= -90 && latitude <= 90;
}

function isValidLongitude(longitude: number | null): longitude is number {
  return longitude !== null && longitude >= -180 && longitude <= 180;
}

export default function PassportHotelSelection({
  experienceOfferId,
  experienceOffer,
  selectedOfferIds,
  savedOfferIds,
  onSelectionChange,
}: Props) {
  const [latitudeValue, setLatitudeValue] = useState('');
  const [longitudeValue, setLongitudeValue] = useState('');
  const [radiusKm, setRadiusKm] = useState(DEFAULT_RADIUS_KM);
  const [submittedSearch, setSubmittedSearch] = useState<NearbyHotelOffersParams>();
  const previousExperienceOfferIdRef = useRef(experienceOfferId);

  const hasExperience = !!experienceOfferId;
  const latitude = parseCoordinate(latitudeValue);
  const longitude = parseCoordinate(longitudeValue);
  const validCenter = isValidLatitude(latitude) && isValidLongitude(longitude) ? { latitude, longitude } : undefined;
  const hasValidRadius = radiusKm >= MIN_RADIUS_KM && radiusKm <= MAX_RADIUS_KM;
  const canSearch = hasExperience && !!validCenter && hasValidRadius;

  const { data, error, fetchNextPage, hasNextPage, isError, isFetching, isFetchingNextPage, isSuccess, refetch } =
    useGetNearbyHotelOffers(submittedSearch);

  useEffect(() => {
    const previousExperienceOfferId = previousExperienceOfferIdRef.current;

    if (previousExperienceOfferId !== experienceOfferId) {
      setSubmittedSearch(undefined);

      const savedOfferIdsSet = new Set(savedOfferIds);
      const selectedSavedOfferIds = selectedOfferIds.filter((offerId) => savedOfferIdsSet.has(offerId));
      if (selectedSavedOfferIds.length !== selectedOfferIds.length) {
        onSelectionChange(selectedSavedOfferIds);
      }
    }

    previousExperienceOfferIdRef.current = experienceOfferId;
  }, [experienceOfferId, onSelectionChange, savedOfferIds, selectedOfferIds]);

  useEffect(() => {
    if (!experienceOfferId) {
      setLatitudeValue('');
      setLongitudeValue('');
      return;
    }

    const nextLatitude = experienceOffer?.location?.latitude;
    const nextLongitude = experienceOffer?.location?.longitude;

    if (isValidLatitude(nextLatitude ?? null) && isValidLongitude(nextLongitude ?? null)) {
      setLatitudeValue(String(nextLatitude));
      setLongitudeValue(String(nextLongitude));
    } else {
      setLatitudeValue('');
      setLongitudeValue('');
    }
  }, [experienceOfferId, experienceOffer?.location?.latitude, experienceOffer?.location?.longitude]);

  useEffect(() => {
    setSubmittedSearch(undefined);
  }, [latitudeValue, longitudeValue, radiusKm]);

  const nearbyHotelOffers = useMemo(() => {
    const offersById = new Map<
      string,
      {
        id: string;
        propertyId: string;
        title: string;
        distanceMeters: number;
        offerType: NearbyHotelOffer['offerType'];
      }
    >();

    data?.pages.forEach((page) =>
      page.result.forEach((offer) => {
        if (!offersById.has(offer.offerId)) {
          offersById.set(offer.offerId, {
            id: offer.offerId,
            propertyId: offer.propertyId,
            title: offer.title,
            distanceMeters: offer.distanceMeters,
            offerType: offer.offerType,
          });
        }
      }),
    );

    return Array.from(offersById.values());
  }, [data?.pages]);

  const { imageIdsByOfferId } = useGetNearbyHotelOfferImages(
    nearbyHotelOffers.map((offer) => ({
      offerId: offer.id,
      offerType: offer.offerType,
    })),
  );

  const hotelOffers = useMemo(
    () =>
      nearbyHotelOffers.map((offer) => ({
        ...offer,
        imageId: imageIdsByOfferId.get(offer.id),
      })),
    [imageIdsByOfferId, nearbyHotelOffers],
  );

  const handleSearch = () => {
    if (!canSearch || !validCenter) {
      return;
    }

    const nextSearch = {
      latitude: validCenter.latitude,
      longitude: validCenter.longitude,
      radiusMeters: radiusKm * METERS_PER_KILOMETRE,
    };

    if (
      submittedSearch &&
      submittedSearch.latitude === nextSearch.latitude &&
      submittedSearch.longitude === nextSearch.longitude &&
      submittedSearch.radiusMeters === nextSearch.radiusMeters
    ) {
      void refetch();
      return;
    }

    setSubmittedSearch(nextSearch);
  };

  return (
    <Stack spacing={2}>
      {!hasExperience && (
        <Typography variant="body2" color="text.secondary">
          Select an experience offer before searching for nearby hotels.
        </Typography>
      )}

      <Box
        aria-disabled={!hasExperience}
        sx={{
          bgcolor: hasExperience ? 'background.paper' : 'action.disabledBackground',
          borderRadius: 1,
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 3fr)' },
          opacity: hasExperience ? 1 : 0.65,
          p: 2,
        }}
      >
        <Stack spacing={2}>
          <TextField
            disabled={!hasExperience}
            fullWidth
            label="Latitude"
            onChange={(event) => setLatitudeValue(event.target.value)}
            slotProps={{ htmlInput: { min: -90, max: 90, step: 'any' } }}
            type="number"
            value={latitudeValue}
          />
          <TextField
            disabled={!hasExperience}
            fullWidth
            label="Longitude"
            onChange={(event) => setLongitudeValue(event.target.value)}
            slotProps={{ htmlInput: { min: -180, max: 180, step: 'any' } }}
            type="number"
            value={longitudeValue}
          />
          <Box>
            <Typography gutterBottom>Radius: {radiusKm} km</Typography>
            <Slider
              aria-label="Radius in kilometres"
              disabled={!hasExperience}
              marks={[
                { value: MIN_RADIUS_KM, label: '1 km' },
                { value: MAX_RADIUS_KM, label: '40 km' },
              ]}
              max={MAX_RADIUS_KM}
              min={MIN_RADIUS_KM}
              onChange={(_, value) => setRadiusKm(value as number)}
              step={1}
              value={radiusKm}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value} km`}
            />
          </Box>
          <Box>
            <Button disabled={!canSearch || isFetching} onClick={handleSearch} variant="contained" color="primary">
              Search
            </Button>
          </Box>
        </Stack>

        <Box
          sx={{
            alignItems: 'center',
            bgcolor: 'grey.50',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            height: { xs: 280, md: 360 },
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {hasExperience && validCenter && hasValidRadius ? (
            <ProximityMap
              latitude={validCenter.latitude}
              longitude={validCenter.longitude}
              name={experienceOffer?.title ?? 'Selected experience'}
              radiusMeters={radiusKm * METERS_PER_KILOMETRE}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" textAlign="center" px={2}>
              Enter valid latitude and longitude values to preview the search area.
            </Typography>
          )}
        </Box>
      </Box>

      {isFetching && hotelOffers.length === 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
          <CircularProgress aria-label="Searching nearby hotels" />
        </Box>
      )}

      {isError && (
        <Alert severity="error">
          Unable to search for nearby hotels. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      )}

      {isSuccess && !isFetching && hotelOffers.length === 0 && (
        <Box p={3} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            No hotel offers were found within this search area.
          </Typography>
        </Box>
      )}

      {hotelOffers.length > 0 && (
        <Stack spacing={2}>
          <TagAssignmentsOfferList
            offers={hotelOffers}
            selectedOfferIds={selectedOfferIds}
            onSelectionChange={onSelectionChange}
            savedOfferIds={savedOfferIds}
            offerLabel="Hotels"
            emptyMessage="No hotel offers were found within this search area."
          />
          {hasNextPage && (
            <Box textAlign="center">
              <Button disabled={isFetchingNextPage} onClick={() => void fetchNextPage()} variant="text" color="primary">
                {isFetchingNextPage ? 'Loading more hotels…' : 'Load more hotels'}
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </Stack>
  );
}
